import {
  UserOutlined,
  SettingOutlined,
  MessageOutlined,
  BookOutlined,
  HeartOutlined,
  BellOutlined,
  FileTextOutlined,
  SearchOutlined,
  CommentOutlined,
  TeamOutlined,
  ExperimentOutlined,
  FileProtectOutlined,
  FormOutlined,
  InboxOutlined,
  ProjectOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
} from '@ant-design/icons';

// 管理中心统一路径前缀
export const ADMIN_PATH_PREFIX = '/admin';

// 需要展示未读消息角标的菜单项标记
export const UNREAD_BADGE_KEY = 'unreadCount';

// 机构服务大类编码（与注册时的服务大类字典一致）
const ORG_CATEGORY_INSPECTION = 'B03A04'; // 检验检测机构
const ORG_CATEGORY_CERTIFICATION = 'B03A03'; // 认证认可机构

/**
 * 解析登录身份
 *
 * 判定优先级：
 *   1. 后端 userType === 2（登录类型：1 个人 / 2 机构）→ 机构账号
 *   2. roleCode 作为补充标识：company / practitioner 归机构侧，expert 归专家
 *   3. 其余 → 个人用户
 *
 * 返回：
 *   isOrgAccount    - 是否机构侧账号（含机构从业人员）
 *   isPersonal      - 是否普通个人
 *   isExpert        - 是否专家
 *   isPractitioner  - 是否机构从业人员
 *   isInspectionOrg - 检验检测机构（B03A04）
 *   isCertOrg       - 认证认可机构（B03A03）
 */
export const getUserIdentity = (userInfo) => {
  const roleCode = userInfo?.roleCode || '';
  const userType = Number(userInfo?.userType);
  const orgCategory = userInfo?.orgProfile?.category || userInfo?.checkOrgInfo?.category || '';

  const isPractitioner = roleCode === 'practitioner';
  const isOrgAccount = userType === 2 || roleCode === 'company' || isPractitioner;
  const isExpert = !isOrgAccount && roleCode === 'expert';

  return {
    roleCode,
    userType,
    orgCategory,
    isOrgAccount,
    isPersonal: !isOrgAccount && !isExpert,
    isExpert,
    isPractitioner,
    isInspectionOrg: orgCategory === ORG_CATEGORY_INSPECTION,
    isCertOrg: orgCategory === ORG_CATEGORY_CERTIFICATION,
  };
};

/**
 * 登录后的功能菜单配置
 * 前台右上角下拉菜单与管理中心左侧菜单共用同一份配置，保证两处模块完全一致
 *
 * 按登录身份区分：
 *   个人 / 专家        → 「个人服务」
 *   机构（含从业人员） → 「机构管理」+「账号服务」
 *
 * 菜单项字段说明：
 *   key         - 唯一标识
 *   label       - 显示文案
 *   icon        - 图标
 *   path        - 跳转路径（不含 /admin 前缀）
 *   badgeKey    - 需要角标的菜单项标记
 *   placeholder - 功能开发中，仅提示不跳转
 *
 * 返回分组数组：[{ key, title, items: [...] }]
 */
export const getUserMenuGroups = (userInfo) => {
  const { isOrgAccount, isExpert, isPractitioner, isInspectionOrg, isCertOrg } =
    getUserIdentity(userInfo);

  // 两种身份都需要的基础账号项
  const messageItem = {
    key: 'messages',
    label: '我的消息',
    icon: <BellOutlined />,
    path: '/my-messages',
    badgeKey: UNREAD_BADGE_KEY,
  };
  const settingsItem = {
    key: 'settings',
    label: '账号设置',
    icon: <SettingOutlined />,
    path: '/account-settings',
  };

  // 机构账号：只展示机构相关业务
  // 组内顺序：人员 → 能力资源（实验室 / 设备 / 标准物质）→ 业务办理（样品 / 项目）→ 资质凭证
  if (isOrgAccount) {
    return [
      {
        key: 'organization',
        title: '机构管理',
        items: [
          ...(isInspectionOrg
            ? [
                { key: 'project-management', label: '项目管理', icon: <ProjectOutlined />, path: '/project-management' },
                { key: 'organization-staff', label: '从业人员', icon: <TeamOutlined />, path: '/organization-staff' },
                {
                  key: 'organization-laboratory',
                  label: '实验室',
                  icon: <ExperimentOutlined />,
                  path: '/organization-laboratory',
                },
                { key: 'device-management', label: '设备管理', icon: <ToolOutlined />, path: '/device-management' },
                {
                  key: 'material-management',
                  label: '标准物质管理',
                  icon: <InboxOutlined />,
                  path: '/material-management',
                },
                { key: 'sample-management', label: '样品管理', icon: <FileTextOutlined />, path: '/sample-management' },
              ]
            : []),
          ...(isCertOrg
            ? [
                {
                  key: 'certification-project',
                  label: '认证认可项目管理',
                  icon: <SafetyCertificateOutlined />,
                  path: '/certification-project',
                },
              ]
            : []),
          {
            key: 'organization-quality-cert',
            label: '资质认定证书',
            icon: <FileProtectOutlined />,
            path: '/organization-quality-cert',
          },
          ...(isPractitioner
            ? [
                { key: 'reports', label: '我的报告', icon: <FileTextOutlined />, placeholder: true },
                { key: 'material', label: '物料查询', icon: <SearchOutlined />, placeholder: true },
              ]
            : []),
        ],
      },
      {
        key: 'account',
        title: '账号服务',
        items: [
          { key: 'profile', label: '个人中心', icon: <UserOutlined />, path: '/profile' },
          messageItem,
          settingsItem,
          { key: 'evaluation', label: '反馈评价', icon: <FormOutlined />, path: '/evaluation' },
        ],
      },
    ];
  }

  // 个人 / 专家：只展示个人相关业务
  // 组内顺序：个人资料 → 咨询 → 学习（培训 / 考试）→ 收藏 → 消息与设置
  return [
    {
      key: 'personal',
      title: '个人服务',
      items: [
        { key: 'profile', label: '个人中心', icon: <UserOutlined />, path: '/profile' },
        { key: 'consultations', label: '我的咨询', icon: <MessageOutlined />, path: '/my-consultations' },
        ...(isExpert
          ? [{ key: 'my-replies', label: '我的回复', icon: <CommentOutlined />, path: '/expert-consultations' }]
          : []),
        { key: 'trainings', label: '我的培训', icon: <BookOutlined />, path: '/my-trainings' },
        { key: 'exams', label: '我的考试', icon: <FileTextOutlined />, path: '/my-exam-list' },
        { key: 'favorites', label: '我的收藏', icon: <HeartOutlined />, path: '/my-favorites' },
        messageItem,
        settingsItem,
      ],
    },
  ];
};

/** 扁平化后的菜单项（前台右上角下拉菜单使用） */
export const getFlatUserMenuItems = (userInfo) =>
  getUserMenuGroups(userInfo).reduce((acc, group) => acc.concat(group.items), []);

/** 进入管理中心时的默认落地页（按身份区分，避免机构用户落到个人中心） */
export const getDefaultAdminPath = (userInfo) => {
  const { isOrgAccount } = getUserIdentity(userInfo);
  return isOrgAccount ? '/organization-staff' : '/profile';
};
